/**
 * The plan strip (MOBILE-ARCHITECTURE §4.3): below 1152 px the plan panel is a sheet, and this one-row strip over the Decide step bar is how it is reached.
 * It says the headline in words ("≈ −$0.22 a share vs your standing policy"), the limit status and "See plan". It keeps one height from first paint to the last
 * recompute: figures sit in a fixed-height row, the last value stays on screen while a new one is computed (the progress line says so), and before the forecast
 * is committed the same row says the gate in words.
 */
import { useRef, type ReactNode } from "react";
import type { LimitState } from "../theme/StateChip";
import { formatUsdPerShare } from "../format";

const MARK: Readonly<Record<LimitState, string>> = { clear: "✓", watch: "▲", flag: "●" };
export const STRIP_LOCKED_LINE = "Forecast locked: make your call first";
export const STRIP_NO_LIMIT = "No limit reached";
/** Before the first real figure exists: the gate in words, never a placeholder that reads as a real zero. */
export const STRIP_WAITING_LINE = "Working out your plan\u2026";
const BASIS = <>a share vs <span className="gx-pstrip-your">your </span>standing policy</>;

export function PlanStrip({ committed, headline, status, computing, onOpen, sheetId, open }: {
  readonly committed: boolean;
  /** The three-year value in dollars a share against the standing policy; null before a figure exists. */
  readonly headline: number | null;
  readonly status: { readonly state: LimitState; readonly text: string } | null;
  readonly computing: boolean;
  readonly onOpen: () => void;
  readonly sheetId?: string;
  readonly open: boolean;
}) {
  // A recompute keeps the last figure on the strip (the progress line says it is moving); only the very first paint has none.
  const held = useRef<number | null>(null);
  if (!committed) held.current = null;
  else if (headline !== null) held.current = headline;
  const shown = headline ?? held.current;
  let line: ReactNode;
  let sub: ReactNode = null;
  if (!committed) line = <span className="gx-pstrip-lock">{STRIP_LOCKED_LINE}</span>;
  else if (shown === null) line = <span className="gx-pstrip-lock" data-testid="decide.strip.waiting">{STRIP_WAITING_LINE}</span>;
  else line = <span className="gx-pstrip-fig"><b className="gx-num" data-testid="decide.strip.figure">{"≈ "}{formatUsdPerShare(shown, { signed: true })}</b> <span className="gx-pstrip-basis gx-caption">{BASIS}</span></span>;
  if (committed) sub = status === null ? <span className="gx-pstrip-st gx-caption" data-state="clear">{MARK.clear} {STRIP_NO_LIMIT}</span> : <span className="gx-pstrip-st gx-caption" data-state={status.state} data-testid="decide.strip.status">{MARK[status.state]} {status.text === "" ? STRIP_NO_LIMIT : status.text}</span>;
  return (
    <div className="gx-pstrip" data-testid="decide.strip" data-state={!committed ? "locked" : computing ? "computing" : "ready"} aria-busy={computing}>
      {computing ? <div className="gx-plan-progress" role="progressbar" aria-label="Updating" /> : null}
      <div className="gx-pstrip-t">{line}{sub}</div>
      <button type="button" className="gx-pstrip-btn" data-testid="decide.strip.open" aria-haspopup="dialog" aria-expanded={open} aria-controls={sheetId} onClick={onOpen}>See plan {"›"}</button>
    </div>
  );
}
